import React, { FC } from 'react';

import IconUser from '@/assets/user.svg?react';

import BackArrow from '@/assets/back-arrow.svg?react';
import IconEnvelope from '@/assets/envelope.svg?react';
import IconFolder from '@/assets/folder.svg?react';
import IconHome from '@/assets/home.svg?react';
import IconPhone from '@/assets/phone.svg?react';
import IconWheel from '@/assets/wheel.svg?react';
import Opinion from '@/components/Opinion';
import Avatar from '@/components/ui/Avatar';
import Button from '@/components/ui/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/Tooltip';
import { CRM_LINK, RESPONSIVE_QUERY } from '@/consts';
import getHelpfindPhone from '@/helpers/getHelpfindPhone';
import { shortenEmail } from '@/helpers/shortenEmail';
import cn from '@/utils/cn';

import { useLingui } from '@lingui/react';
import { useMediaQuery } from 'react-responsive';

export type UserData = {
  name: string;
  car?: string;
  carYear?: string;
  caseID?: number | string;
  phoneNumber?: string;
  email?: string;

  issueTypeHH?: string;
};

export type Guardian = {
  name: string;
  image: string;
  email?: string;
  phoneNumber?: string;
  jobDescription?: string;
};

type CrmUser = {
  name: string;
  avatar: string;
};

interface Props {
  userData: UserData;
  guardian: Guardian;
  className?: string;
  crmUser?: CrmUser;
  leadID?: number;
  opportunityID?: number;
  purchaseID?: number;
  surcharge?: number;
  isSummaryForm?: boolean;
  guardianTitle?: string;
  claimPanelVariant?: boolean;
  onLogoutClick?: () => void;
  productId?: number;
  showGuardian?: boolean;
}

const ClientCard: FC<Props> = ({
  userData,
  guardian,
  className,
  crmUser,
  leadID,
  opportunityID,
  purchaseID,
  surcharge,
  isSummaryForm,
  guardianTitle = 'Opiekun Twojej sprawy',
  claimPanelVariant,
  onLogoutClick,
  productId,
  showGuardian = true,
}) => {
  const { i18n } = useLingui();
  const id = purchaseID ?? opportunityID ?? leadID;

  return (
    <div>
      <div
        className={cn(
          'flex flex-col gap-6 rounded-2xl border-primary bg-gray-100 p-6 pt-[26px] md:gap-3 lg:flex lg:flex-row lg:gap-5 lg:rounded-[10px] lg:border-t-[6px] lg:py-6',
          className
        )}
      >
        <div className="lg:flex-1">
          <h3 className="caption pb-3 text-primary">
            {isSummaryForm
              ? i18n.t('Czekają na Ciebie dodatkowe środki')
              : opportunityID
                ? i18n.t('Twoja propozycja')
                : i18n.t('Twoje dane')}
          </h3>
          <>
            <div className="flex max-w-[470px] flex-col gap-2 md:flex-row md:gap-x-7">
              <div className={'space-y-2 truncate lg:space-y-2.5'}>
                {!isSummaryForm && (
                  <Cell
                    icon={
                      <IconUser className="min-w-4 text-primary" width={16} />
                    }
                    label={userData?.name ?? '-'}
                    className="break-words md:order-1"
                    dataTestId="client-name"
                  />
                )}
                {isSummaryForm && (
                  <Cell
                    icon={
                      <IconUser className="min-w-4 text-primary" width={16} />
                    }
                    spanClassName="max-w-none lg:max-w-none"
                    label={
                      <p>
                        {i18n.t('Dodatkowe środki:')}{' '}
                        <span className="font-semibold">
                          {surcharge?.toLocaleString('PL', {
                            style: 'currency',
                            currency: 'PLN',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </span>
                      </p>
                    }
                    className="break-words md:order-1"
                  />
                )}
                {(userData?.car || userData.carYear) && (
                  <Cell
                    icon={
                      <IconWheel className="min-w-4 text-primary" width={16} />
                    }
                    label={`${userData.car?.length === 0 ? '-' : userData.car}${userData.carYear === 'NaN' ? '' : `, ${userData.carYear} r.`}`}
                    className="md:order-3"
                  />
                )}
                {userData?.issueTypeHH && (
                  <Cell
                    icon={
                      <IconHome className="min-w-4 text-primary" width={16} />
                    }
                    label={userData.issueTypeHH}
                    className="md:order-3"
                  />
                )}
                {userData?.caseID && (
                  <Cell
                    icon={
                      <IconFolder className="min-w-4 text-primary" width={16} />
                    }
                    label={`${i18n.t('Nr sprawy:')} ${userData.caseID}`}
                    className="md:order-3"
                  />
                )}
              </div>
              <div className={'space-y-2 truncate lg:space-y-2.5'}>
                <Cell
                  icon={
                    <IconPhone className="min-w-4 text-primary" width={16} />
                  }
                  label={userData?.phoneNumber ?? '-'}
                  className="md:order-2"
                  dataTestId="client-phone"
                />
                <EmailCell
                  email={userData.email ?? ''}
                  car={userData.car ?? ''}
                  name={userData.name ?? ''}
                  dataTestId="client-email"
                />
              </div>
            </div>
          </>
        </div>
        {showGuardian && (
          <div className="shrink-0">
            <h3 className="caption pb-3 text-primary">{guardianTitle}</h3>
            <div className="flex flex-col items-start gap-3 text-dark-gray-500 xxs:flex-row xxs:items-center xxs:gap-4">
              <Avatar img={guardian.image} name={guardian.name} />
              <div>
                <h5 className="pb-0.5 lg:pb-1">{guardian.name}</h5>
                <GuardianInfo
                  email={guardian.email}
                  phoneNumber={guardian.phoneNumber}
                  jobDescription={guardian.jobDescription}
                  className="flex"
                />
              </div>
            </div>
          </div>
        )}
        {crmUser ? (
          <div className="flex flex-col gap-2.5 lg:hidden">
            <div className="flex items-center gap-x-3">
              <Avatar img={crmUser.avatar} name={crmUser.name} />
              <p className="font-jakarta text-base font-semibold leading-6 text-dark-gray-500">
                Witaj, {crmUser.name}
              </p>
            </div>
            <a
              href={`${CRM_LINK}/leads/${leadID}/edit`}
              target="_blank"
              className={'flex items-center gap-x-0.5'}
              rel="noreferrer"
            >
              <Button size="small" className="w-full">
                <BackArrow className={'size-4.5'} />
                <span>Wróć do CRM</span>
              </Button>
            </a>
          </div>
        ) : (
          !claimPanelVariant && (
            <Button asChild className="lg:hidden">
              <a href={`tel:${getHelpfindPhone(productId)}`}>
                <IconPhone width={18} className="shrink-0" />
                {getHelpfindPhone(productId)}
              </a>
            </Button>
          )
        )}
        {surcharge && (
          <h4 className="h4 flex justify-center gap-1 text-center text-dark-gray-500 lg:hidden">
            {i18n.t('Dodatkowe środki:')}{' '}
            <span className="text-yellow-500">
              {surcharge.toLocaleString('PL', {
                style: 'currency',
                currency: 'PLN',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}{' '}
            </span>
          </h4>
        )}
        {claimPanelVariant && onLogoutClick && (
          <Button
            size={'small'}
            asChild
            className="cursor-pointer lg:hidden"
            onClick={onLogoutClick}
          >
            <div>
              <IconUser width={18} className="shrink-0" />
              {i18n.t('Wyloguj się')}{' '}
            </div>
          </Button>
        )}
        {!claimPanelVariant && (
          <div className="lg:hidden">
            <Opinion />
          </div>
        )}
        {id && (
          <p className="caption-large flex justify-center text-center text-dark-gray-400 lg:hidden">
            {purchaseID
              ? i18n.t('Numer Twojej dopłaty:')
              : opportunityID
                ? i18n.t('Numer Twojej propozycji:')
                : i18n.t('Numer Twojej weryfikacji:')}{' '}
            {id}
          </p>
        )}{' '}
      </div>
      <span className="flex w-full pt-4 xxs:hidden" />
    </div>
  );
};

export default ClientCard;

const GuardianInfo = ({
  email,
  jobDescription,
  className,
}: {
  email?: string;
  phoneNumber?: string;
  className?: string;
  jobDescription?: string;
}) => (
  <div
    className={cn(
      'flex flex-col gap-2 md:flex-row md:gap-3 md:pr-16',
      className
    )}
  >
    {email && (
      <div className="body lg:body-small">
        <a
          className="flex items-center gap-2 transition-colors hover:text-primary"
          href={`mailto:${email}`}
        >
          <IconEnvelope className="shrink-0 text-primary" width={16} />
          {email}
        </a>
      </div>
    )}
    {jobDescription && !email && (
      <div className="body lg:body-small">{jobDescription}</div>
    )}
  </div>
);

const EmailCell = ({
  email,
  car,
  name,
  dataTestId,
}: {
  email: string;
  car: string;
  name: string;
  dataTestId?: string;
}) => {
  const isDesktop = useMediaQuery({
    query: RESPONSIVE_QUERY.IS_DESKTOP,
  });
  const lengthChecker = car.length < name.length ? name.length : car.length;
  if (email && shortenEmail(email, lengthChecker) !== email) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger>
            {isDesktop ? (
              <Cell
                icon={
                  <IconEnvelope className="min-w-4 text-primary" width={16} />
                }
                label={shortenEmail(email, lengthChecker)}
                className="hidden md:order-4 lg:flex lg:max-w-[300px]"
                spanClassName="lg:max-w-[300px]"
                dataTestId={dataTestId}
              />
            ) : (
              <Cell
                icon={
                  <IconEnvelope className="min-w-4 text-primary" width={16} />
                }
                label={email}
                className="md:order-4 lg:hidden lg:max-w-[300px]"
                spanClassName="lg:max-w-[300px]"
                dataTestId={dataTestId}
              />
            )}
          </TooltipTrigger>
          <TooltipContent>
            <p>{email}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return (
    <>
      {isDesktop ? (
        <Cell
          icon={<IconEnvelope className="min-w-4 text-primary" width={16} />}
          label={email ? shortenEmail(email, lengthChecker) : '-'}
          className="hidden md:order-4 lg:flex"
          spanClassName="lg:max-w-[300px]"
          dataTestId={dataTestId}
        />
      ) : (
        <Cell
          icon={<IconEnvelope className="min-w-4 text-primary" width={16} />}
          label={email || '-'}
          className="md:order-4 lg:hidden"
          spanClassName="lg:max-w-[300px]"
          dataTestId={dataTestId}
        />
      )}
    </>
  );
};

const Cell = ({
  icon,
  label,
  className,
  spanClassName,
  dataTestId,
}: {
  icon: React.ReactNode;
  label: React.ReactNode | string;
  className?: string;
  spanClassName?: string;
  dataTestId?: string;
}) => {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      {icon}
      <span
        className={cn(
          'body lg:body-small min-w-[136px] max-w-[200px] truncate xxs:max-w-[320px] lg:max-w-[200px]',
          spanClassName
        )}
        data-testid={dataTestId}
      >
        {label}
      </span>
    </div>
  );
};
